import axios from 'axios';
import {getUserName} from "./GeneralUtils";
import {ACC_API_URL, ACC_API_KEY} from "../constants";
import {clearAccSession, ensureAccSession, getAccSession, notifyAccSessionExpired} from "../accSession";
import {getGoogleAccessToken} from "../googleAccessToken";
import {KIOSK_OFF_NOTICE, KIOSK_SIGNED_OUT_NOTICE, dropKioskIdentity, isKioskSession} from "../kioskFlag";

/**
 * Commands whose args or response carry a secret: a protected code value on
 * the way in (`set_protected_code`) or out (`reveal_protected_code`), or a
 * Google token, or a kiosk PIN (`start_kiosk_session`, `set_trainer_pin`). The
 * console is unaudited and one DevTools tab away on the shared iPad, so for
 * these the log shows the command and nothing else.
 */
export const SECRET_COMMANDS = new Set([
    'reveal_protected_code',
    'set_protected_code',
    'start_acc_session',
    'start_kiosk_session',
    'set_trainer_pin',
]);

const isSecret = (body) => SECRET_COMMANDS.has(body?.command);

/** The session is a bearer credential; it must never reach the console or a log drain. */
const redactSession = (body) => (body.session ? {...body, session: '[redacted]'} : body);

const redactBody = (body) => {
    const safe = redactSession(body);
    return isSecret(body) && 'args' in safe ? {...safe, args: '[redacted]'} : safe;
};

/** For a secret command the axios error object carries the request body in `config.data`. */
const loggableError = (body, error) => (isSecret(body)
    ? {command: body.command, message: error?.message, status: error?.response?.status, data: '[redacted]'}
    : error);

const KIOSK_DISABLED = 'E_KIOSK_DISABLED';

/**
 * The `errorCode` a 401 body carries, whether axios parsed it or left the Lambda's JSON string.
 * The flag-off 401 is the one refusal that is neither: a plain `E_KIOSK_DISABLED: <message>` string.
 */
const refusalCode = (error) => {
    let body = error?.response?.data;
    if (typeof body === 'string') {
        try {
            body = JSON.parse(body);
        } catch (e) {
            return body.startsWith(KIOSK_DISABLED) ? KIOSK_DISABLED : null;
        }
    }
    return body?.errorCode || null;
};

const post = async (requestBody) => {
    console.log("executeCommand - Sending to:", ACC_API_URL);
    console.log("executeCommand - Request body:", JSON.stringify(redactBody(requestBody), null, 2));
    try {
        const response = await axios.post(ACC_API_URL, requestBody, {
            headers: {
                'x-api-key': ACC_API_KEY
            }
        });
        console.log("executeCommand - Response:", isSecret(requestBody) ? '[redacted]' : response.data);
        return response.data;
    } catch (error) {
        console.error("Error executing command:", loggableError(requestBody, error));
        // The rethrown axios error is not safe either: `config.data` is the request body, PIN
        // included, and anything upstream that logs or serialises the error would print it.
        if (isSecret(requestBody) && error?.config) {
            error.config = {...error.config, data: undefined};
        }
        throw error;
    }
};

export const executeCommand = async (command, args, options = {}) => {
    // Identity is still sent as `user_name` because the server accepts it until
    // REQUIRE_ACC_SESSION flips. When a session token is present the server prefers it and
    // ignores the body, which is what makes the operator's identity unforgeable.
    //
    // `anonymous` is for the commands that run before anybody is signed in (the kiosk roster and
    // the PIN exchange): no stored session rides along, none is minted, and a 401 is not read
    // as an expired identity.
    const anonymous = options.anonymous === true;
    let session = anonymous ? null : getAccSession();
    // A tab holding a live Google token but no session can still mint one - after a 401 dropped
    // the old token, say. Without this the command goes out unsigned, which is a 401 of its own
    // once the server requires the session.
    if (!anonymous && !session && getGoogleAccessToken()) {
        await ensureAccSession().catch(() => undefined);
        session = getAccSession();
    }
    const userName = anonymous ? null : getUserName();
    const requestBody = {
        command,
        user_name: userName,
        args
    };
    if (session) {
        requestBody.session = session;
    }

    // Add optional fields if provided
    if (options.userId) {
        requestBody.user_id = options.userId;
    }
    if (options.reason) {
        requestBody.reason = options.reason;
    }

    try {
        return await post(requestBody);
    } catch (error) {
        if (error?.response?.status === 401 && !anonymous) {
            // A PIN session has no Google token behind it, so there is nothing to re-mint: drop
            // the identity and hand the device back to the name grid.
            if (isKioskSession()) {
                clearAccSession();
                // A manager turned the kiosk off mid-shift: say that, not a generic lapse.
                dropKioskIdentity(refusalCode(error) === KIOSK_DISABLED ? KIOSK_OFF_NOTICE : KIOSK_SIGNED_OUT_NOTICE);
                throw error;
            }
            // A 401 on a request that carried a session means the token is invalid or expired.
            // The Google token behind it lives about an hour against this app's four, so
            // re-minting is the difference between one lapsed request and a trainer whose every
            // command 401s in silence for the rest of the shift. At most one re-mint and one
            // retry per command: a token the server keeps refusing would otherwise spin here.
            if (session) {
                clearAccSession();
                const reminted = await ensureAccSession().catch(() => false);
                const fresh = reminted ? getAccSession() : null;
                if (fresh) {
                    return post({...requestBody, session: fresh});
                }
            }
            // Nobody signed in, or nothing left to trade: every later command fails the same
            // way, so hand the operator back to sign-in instead of leaving a dashboard that
            // looks signed in and rejects everything. The `user` cookie lapses four hours into
            // a shift and without this each panel on the shared iPad discovers it separately —
            // around ninety 401s in ten minutes, ninety little errors. The signal itself is
            // debounced, so a burst produces one takeover.
            //
            // Having no session is not itself the tell. Until `REQUIRE_ACC_SESSION` flips, the
            // legacy `user_name` path is the normal state for a signed-in trainer, so treating
            // a session-less 401 as an expired identity would sign the gym out over one
            // transient rejection.
            if (!userName || !getGoogleAccessToken()) {
                notifyAccSessionExpired();
            }
        }
        throw error;
    }
};
